import { Download, Upload, FileText } from 'lucide-react';
import DokladNahled from '../../components/doklady/DokladNahled';
import { ACCENT as SECTION_COLOR, OK, WARN } from '../../lib/uiTokens';

/**
 * Karta skenu účtenky — první karta třetího sloupce.
 *
 * Norma chce ve třetím sloupci to, co k položce jen informuje. U suroviny je
 * to cena, u fleku hotovost — u dokladu **obrázek účtenky**. Když se do
 * dokladu ťukají položky, člověk se na něj dívá při každém řádku.
 *
 * Data si karta netahá: sloupec je natáhne jednou pro všechny tři karty
 * (`DokladPanelSloupec`). Dokud si je brala tahle, byly zaúčtování a historie
 * jejími pruhy — jedna karta se třemi hlavičkami, kterou nešlo výškově
 * rozdělit, takže sken dostal jen to, co po nich zbylo.
 *
 * Karta vyplní, co jí sloupec dá: hlavička a patička drží výšku, náhled mezi
 * nimi scrolluje. Na vyšší obrazovce je tedy sken větší.
 */
export default function DokladSkenPanel({ doklad, dokladId, cardStyle, isDark, mutedColor, onNahrat, kompaktni = false }) {
    const prilohy = doklad?.prilohy || [];
    // „Stáhnout" musí dát tentýž dokument, jaký je v náhledu — tedy sloučené
    // PDF, ne první řádek v tabulce příloh (u starších dokladů JPG jedné strany).
    const keStazeni = prilohy.find(p => p.mime_type === 'application/pdf') || prilohy[0];
    const hranice = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const jistota = doklad?.ai_confidence_avg != null ? Math.round(Number(doklad.ai_confidence_avg) * 100) : null;

    const radek = (k, v, barva) => (
        <div className="flex items-center justify-between gap-2" style={{ padding: '6px 12px', borderTop: `1px solid ${hranice}` }}>
            <span className="text-[11px]" style={{ color: mutedColor }}>{k}</span>
            <span className="text-[11.5px] tabular-nums" style={{ color: barva ?? 'var(--text-primary)' }}>{v}</span>
        </div>
    );

    return (
        <div className={kompaktni ? 'flex flex-col min-h-0' : 'rounded-xl overflow-hidden flex flex-col min-h-0'}
            style={{ ...(kompaktni ? null : cardStyle), flex: 1, minHeight: 0 }}>
            <div className="flex items-center justify-between flex-shrink-0" style={{ padding: '9px 12px', borderBottom: `1px solid ${hranice}` }}>
                <span className="text-[10.5px] font-bold uppercase tracking-wide" style={{ color: mutedColor }}>Sken dokladu</span>
                {keStazeni && (
                    <a href={`/api/finance/doklady/${dokladId}/prilohy/${keStazeni.id}/stahnout`}
                        className="inline-flex items-center gap-1 text-[11px]" style={{ color: SECTION_COLOR }}>
                        <Download size={12} /> Stáhnout
                    </a>
                )}
            </div>

            <div className="flex-1 min-h-0 overflow-auto" style={{ padding: 10 }}>
                {prilohy.length > 0 ? (
                    <DokladNahled prilohy={prilohy} dokladId={dokladId} />
                ) : (
                    <div className="text-center" style={{ padding: '28px 12px' }}>
                        <FileText size={26} style={{ color: mutedColor, margin: '0 auto 8px' }} />
                        <div className="text-[12px]" style={{ color: 'var(--text-primary)' }}>
                            {doklad ? 'Doklad nemá přílohu' : 'Načítám…'}
                        </div>
                        {doklad && (
                            <div className="text-[11px] mt-1" style={{ color: mutedColor }}>
                                Ruční doklady sken nemají — dá se doplnit.
                            </div>
                        )}
                        {doklad && onNahrat && (
                            <button onClick={onNahrat}
                                className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-lg text-[12px] font-semibold"
                                style={{ background: `${SECTION_COLOR}1f`, color: SECTION_COLOR, border: `1px solid ${SECTION_COLOR}44` }}>
                                <Upload size={13} /> Nahrát přílohu
                            </button>
                        )}
                    </div>
                )}
            </div>

            {doklad && (
                <div style={{ flexShrink: 0 }}>
                    {jistota != null && radek('AI jistota', `${jistota} %`, jistota >= 85 ? OK : WARN)}
                    {doklad.podoba && radek('Podoba', {
                        k_tisku: 'k tisku', fyzicky: 'papír máme', vytisteno: 'vytištěno',
                    }[doklad.podoba] ?? doklad.podoba, doklad.podoba === 'k_tisku' ? WARN : undefined)}
                    {prilohy.length > 1 && radek('Stran', String(prilohy.length))}
                </div>
            )}
        </div>
    );
}
